import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';

export type OrgSummary = {
  readonly id: string;
  readonly name: string;
  readonly industry: string;
  readonly website: string;
  readonly studioCount: number;
};

export type StudioSummary = {
  readonly id: string;
  readonly name: string;
  readonly organizationId: string;
  readonly agentCount: number;
};

export const getStudioSummaries = (roomRecords: Record<string, RoomRecord>): StudioSummary[] => {
  return Object.entries(roomRecords)
    .map(([id, room]) => ({
      id,
      name: room.name ?? 'Untitled Studio',
      organizationId: room.organizationId ?? '',
      agentCount: Object.keys(room.agents ?? {}).length
    }))
    .filter((studio) => studio.organizationId !== '')
    .sort((a, b) => a.id.localeCompare(b.id));
};

/**
 * Summary for one studio, or null when its record is missing or not yet fully
 * streamed (no organizationId). Field mapping and the organizationId filter
 * deliberately mirror getStudioSummaries so per-studio and bulk consumers
 * agree on what an identifiable studio is — a record without its org yet is
 * "not ready", not "unknown org". [ENG-4401]
 */
export const getStudioSummaryById = (
  roomRecords: Record<string, RoomRecord>,
  id: string
): StudioSummary | null => {
  const room = roomRecords[id];
  if (!room) {
    return null;
  }
  const organizationId = room.organizationId ?? '';
  if (organizationId === '') {
    return null;
  }
  return {
    id,
    name: room.name ?? 'Untitled Studio',
    organizationId,
    agentCount: Object.keys(room.agents ?? {}).length
  };
};

export const getOrganizationSummaries = (
  organizations: Record<string, Organization>,
  studios: StudioSummary[]
): OrgSummary[] => {
  const studiosByOrgId = studios.reduce<Record<string, number>>((accumulator, studio) => {
    accumulator[studio.organizationId] = (accumulator[studio.organizationId] ?? 0) + 1;
    return accumulator;
  }, {});

  return Object.entries(organizations)
    .map(([id, org]) => ({
      id,
      name: org.name ?? 'Unnamed Organization',
      industry: org.industry ?? '',
      website: org.website ?? '',
      studioCount: studiosByOrgId[id] ?? 0
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
};
